import { useEffect } from 'react';
import qs from 'qs';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/root-reducer';
import { setGleapUser } from '../../utils';
import { setGleapUserHash } from '../../utils/token';

interface UseGleapUserIdentityWithConfigProps {
  enable?: boolean;
}

const PADDLE_CUSTOMER_PROFILE_BASE_URL =
  'https://vendors.paddle.com/customers-v2';

const APUS_TRULYINBOX_USERS_URL =
  'https://apus.saleshandy.com/trulyinbox-users';

/** Get userId from meta array (API returns meta[].userId); only send to Gleap when found */
const getUserIdFromMeta = (
  meta: Array<{ userId?: number }> | null,
): number | undefined => {
  const item = meta?.find((m) => m.userId != null);
  return item?.userId;
};

const useGleapUserIdentityWithConfig = ({
  enable = true,
}: UseGleapUserIdentityWithConfigProps = {}) => {
  const home = useSelector((state: RootState) => state.home);
  const shAccountId = home?.shAccountId;
  const firstName = home?.firstName;
  const lastName = home?.lastName;
  const email = home?.email ?? home.adminDetails?.email;
  const meta = home?.meta;
  const planName = home?.subscription?.planName || '';
  const customerId = home?.subscription?.customerId;
  /** Paddle-billed accounts; `internal` plans (e.g. Free) are billed through
   *  Paddle too, so they carry a real Paddle customer id. Stripe accounts have
   *  no Paddle profile. */
  const planProvider = home?.subscription?.planProvider;
  const paddleCustomerLink =
    (planProvider === 'paddle' || planProvider === 'internal') && customerId
      ? `${PADDLE_CUSTOMER_PROFILE_BASE_URL}/${customerId}`
      : '';
  const gleapUserHash = home?.gleapUserHash;
  const userId = meta
    ? getUserIdFromMeta(meta as Array<{ userId?: number }>)
    : undefined;
  /** APUS user list pre-filtered to this user, where the agent can run "login as
   *  TrulyInbox user". A plain GET page on the APUS domain, so the agent's own
   *  admin session applies. Empty until the email is loaded, so a half-built
   *  link never reaches Gleap. */
  const apusProfileLink = email
    ? `${APUS_TRULYINBOX_USERS_URL}?${qs.stringify(
        { skip: 0, search: email },
        { skipNulls: true },
      )}`
    : '';

  useEffect(() => {
    if (!enable) return;
    if (gleapUserHash) {
      setGleapUserHash(gleapUserHash);
    }

    setGleapUser({
      shAccountId: String(shAccountId),
      userId,
      firstName,
      lastName,
      email,
      planName,
      paddleCustomerLink,
      apusProfileLink,
    });
  }, [
    enable,
    shAccountId,
    userId,
    firstName,
    lastName,
    email,
    gleapUserHash,
    paddleCustomerLink,
    apusProfileLink,
  ]);

  return null;
};

export default useGleapUserIdentityWithConfig;
